import { ready, type Sodium } from "./sodium";

/**
 * SecureChannel — authenticated, confidential, ordered message framing over the
 * (already E2E-authenticated) WebRTC data channel.
 *
 * Each direction has its own key (from the handshake) and a monotonic counter
 * used as the XChaCha20-Poly1305 nonce. Because the RTCDataChannel is reliable
 * and ordered, the counters stay in lockstep; any tamper, drop, or reorder makes
 * decryption fail and we abort. This is the application-layer E2E layer that sits
 * above WebRTC's DTLS, so neither the signaling server nor a TURN relay can read
 * or alter a single byte.
 */
export class SecureChannel {
  private txCounter = 0n;
  private rxCounter = 0n;

  private constructor(
    private readonly sodium: Sodium,
    private readonly txKey: Uint8Array,
    private readonly rxKey: Uint8Array
  ) {}

  static async create(
    txKey: Uint8Array,
    rxKey: Uint8Array
  ): Promise<SecureChannel> {
    return new SecureChannel(await ready(), txKey, rxKey);
  }

  seal(plaintext: Uint8Array): Uint8Array {
    const nonce = nonceFromCounter(this.txCounter++);
    return this.sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      plaintext,
      null,
      null,
      nonce,
      this.txKey
    );
  }

  /** Decrypt the next inbound frame. Throws if authentication fails. */
  open(ciphertext: Uint8Array): Uint8Array {
    const nonce = nonceFromCounter(this.rxCounter++);
    return this.sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      ciphertext,
      null,
      nonce,
      this.rxKey
    );
  }
}

function nonceFromCounter(counter: bigint): Uint8Array {
  // XChaCha20 nonce is 24 bytes; encode the 64-bit counter in the low bytes.
  const nonce = new Uint8Array(24);
  new DataView(nonce.buffer).setBigUint64(0, counter, true);
  return nonce;
}
